import { Dispatch, SetStateAction, useEffect } from "react";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import { useTranslation } from "react-i18next";

//const ITEM_HEIGHT = 48;
//const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			width: 250,
		},
	},
};

interface Props {
	deviceId: string;
	selectableParameters: string[];
	selectedParameters: string[];
	setSelectedParameters: Dispatch<SetStateAction<string[]>>;
	maxParameters: number;
	type: string;
	width?: string;
	margin?: number;
	deviceModel?: string;
	enforceMutualExclusion?: boolean;
  }
  
  // Component function definition
  export const SelectParameters: React.FC<Props> = ({
	deviceId,
	selectableParameters,
	selectedParameters,
	setSelectedParameters,
	maxParameters,
	type,
	width,
	margin,
	deviceModel,
	enforceMutualExclusion = false
  }) => {
	//console.log ("SelectParameters", selectableParameters, selectedParameters, selectedParameters.length, maxParameters)
	// Selection change event

	const handleChange = (event: SelectChangeEvent<string[]>) => {
		const value = event.target.value;
		// Handle the case where value might be a string (when empty)
		let newSelectedParameters = typeof value === 'string' ? [] : value;
		
		// Only enforce mutual exclusion if the prop is set
		if (enforceMutualExclusion) {
			// Enforce mutual exclusion for IBP channels (only one can be selected at a time)
			const ibpChannels = ['IBP1', 'IBP2', 'IBP3', 'IBP4'];
			const selectedIBPs = newSelectedParameters.filter(p => ibpChannels.includes(p));
			
			if (selectedIBPs.length > 1) {
				const previouslySelectedIBPs = selectedParameters.filter(p => ibpChannels.includes(p));
				const newlySelectedIBP = selectedIBPs.find(ibp => !previouslySelectedIBPs.includes(ibp));
				
				// Remove all IBP channels and add back only the newly selected one
				newSelectedParameters = newSelectedParameters.filter(p => !ibpChannels.includes(p));
				if (newlySelectedIBP) {
					newSelectedParameters.push(newlySelectedIBP);
				}
			}
			
			// Enforce mutual exclusion for ECG leads L1, L2, L3 (only one can be selected at a time)
			const ecgLeads = ['ECG_L1', 'ECG_L2', 'ECG_L3'];
			const selectedECGs = newSelectedParameters.filter(p => ecgLeads.includes(p));
			
			if (selectedECGs.length > 1) {
				const previouslySelectedECGs = selectedParameters.filter(p => ecgLeads.includes(p));
				const newlySelectedECG = selectedECGs.find(ecg => !previouslySelectedECGs.includes(ecg));
				
				// Remove all ECG leads and add back only the newly selected one
				newSelectedParameters = newSelectedParameters.filter(p => !ecgLeads.includes(p));
				if (newlySelectedECG) {
					newSelectedParameters.push(newlySelectedECG);
				}
			}
		}
		
		setSelectedParameters(newSelectedParameters);
		
		const dest = `${type}-${deviceId}`;
		localStorage.setItem(dest, JSON.stringify(newSelectedParameters));
	};
	  
	useEffect(() => {
		//console.log("Selected Parameters updated via useEffect:", selectedParameters);
	  }, [selectedParameters]);
	  
	// Helper function to determine if an item should be disabled due to mutual exclusion
	const isItemDisabled = (itemName: string): boolean => {
		// Only enforce mutual exclusion if the prop is set
		if (!enforceMutualExclusion) {
			return false;
		}
		
		const ibpChannels = ['IBP1', 'IBP2', 'IBP3', 'IBP4'];
		const ecgLeads = ['ECG_L1', 'ECG_L2', 'ECG_L3'];
		
		// Check if any IBP channel is selected and this is a different IBP channel
		const selectedIBP = selectedParameters.find(p => ibpChannels.includes(p));
		if (selectedIBP && ibpChannels.includes(itemName) && itemName !== selectedIBP) {
			return true;
		}
		
		// Check if any ECG lead is selected and this is a different ECG lead
		const selectedECG = selectedParameters.find(p => ecgLeads.includes(p));
		if (selectedECG && ecgLeads.includes(itemName) && itemName !== selectedECG) {
			return true;
		}
		
		return false;
	};

	const { t } = useTranslation();
	return (
		<FormControl sx={{ mt: margin ?? 2, width: width ?? 250 }}>
			<InputLabel
				sx={{ color: "text.primary" }}
				id="demo-multiple-checkbox-label"
			>
				{t("parameters")}
			</InputLabel>
			<Select
				labelId="demo-multiple-checkbox-label"
				id="demo-multiple-checkbox"
				multiple
				value={selectedParameters}
				onChange={handleChange}
				input={<OutlinedInput label={t("parameters")} />}
				renderValue={(selected) =>
					selected
						.map((p) => {
							return t(p);
						})
						.join(", ")
				}
				MenuProps={MenuProps}
			>
			{selectableParameters.map((name) => {
				const isDisabled = isItemDisabled(name);
				return (
					<MenuItem
						key={name}
						value={name}
						disabled={isDisabled}
					>
						<Checkbox
							checked={selectedParameters.includes(name)}
							disabled={isDisabled}
						/>
						<ListItemText 
							primary={t(name)}
							sx={{ color: isDisabled ? 'text.disabled' : 'inherit' }}
						/>
					</MenuItem>
				);
			})}			</Select>
		</FormControl>
	);
}
